import { hasAnalyticsConsent } from './consent';
import { getEnv } from 'lib/helpers/env';

export enum GTMEvent {
  CASE_TRANSITION          = 'Case transition',
  SET_USER_PREF            = 'Set user preference',
  REFERRAL_ERROR           = 'referral_error',
  // CD-8649: the DE-fallback outage state, distinct from "case not found" so the two can be
  // reported separately. Requires a matching GTM container tag — that change is not in this repo.
  REFERRAL_UNAVAILABLE     = 'referral_unavailable',
  // Referral brand resolution (CD-8652). `brand` and `referralSource` ('legacy' for arrivals via
  // the /referral redirect, 'direct' otherwise) become reporting dimensions once the GTM
  // container defines the matching Data Layer Variables. That container change is not in this
  // repo and is still outstanding.
  REFERRAL_BRAND_RESOLVED  = 'referral_brand_resolved',
  DOCUMENT_TITLE_UPDATED   = 'document_title_updated',
  FILE_UPLOAD_SUCCESS      = 'file_upload_success',
  FILE_UPLOAD_FAILURE      = 'file_upload_failure',
  PROOFREAD_REQUESTED      = 'proofread_requested',
  PROOFREAD_ACCEPTED       = 'proofread_accepted',
  PROOFREAD_REJECTED       = 'proofread_rejected',
  PROOFREAD_ERROR          = 'proofread_error',
  // Per-transition events split out of the CASE_TRANSITION catch-all
  COMPLETE_ENQUIRY_HAS_ACCEPTED_OFFER               = 'complete_enquiry_has_accepted_offer',
  COMPLETE_DRAFT_ADDITIONAL_INFO                    = 'complete_draft_additional_info',
  COMPLETE_ENQUIRY_DATE_FIRST_COMPLAINED            = 'complete_enquiry_date_first_complained',
  COMPLETE_DRAFT_EDI_SURVEY                         = 'complete_draft_edi_survey',
  COMPLETE_DRAFT_COMPLAINT_SUMMARY                  = 'complete_draft_complaint_summary',
  COMPLETE_ENQUIRY_OFFER_IMPLEMENTED_UNSATISFACTORY = 'complete_enquiry_offer_implemented_unsatisfactory',
  // Onboarding engagement events (CD-8243)
  NAME_FILLED              = 'name_filled',
  CARES_WIDGET_CLICKED     = 'cares_widget_clicked',
  // DXA events — GTM forwards these from the dataLayer to Decibel Insight (Medallia DXA)
  CONVERSION               = 'conversion',
  CONTINUE_CASE            = 'continue_case',
  CONTACT_US               = 'contact_us',
  CREATE_CASE              = 'create_case',
  // Authentication events (CD-8487) - from CD-7936
  AUTH_SIGN_UP_STARTED = 'auth.sign_up_started',
  AUTH_SIGN_UP_SECTOR_SELECTED = 'auth.sign_up_sector_selected',
  AUTH_SIGN_UP_WITH_SECTOR = 'auth.sign_up_with_sector',
  AUTH_SIGN_UP_COMPLETED = 'auth.sign_up_completed',
  AUTH_SIGN_UP_FAILED = 'auth.sign_up_failed',
  AUTH_SIGN_UP_CONFIRMED = 'auth.sign_up_confirmed',
  AUTH_SIGN_IN_COMPLETED = 'auth.sign_in_completed',
  AUTH_SIGN_IN_FAILED = 'auth.sign_in_failed',
  AUTH_PASSWORD_RESET_REQUESTED = 'auth.password_reset_requested',
  AUTH_PASSWORD_RESET_COMPLETED = 'auth.password_reset_completed',
  AUTH_SIGN_OUT = 'auth.sign_out'
}

export interface EventData {
  event: GTMEvent;
  category?: string;
  action?: string;
  label?: string;
  value?: any;
  [key: string]: any;
}

// CASE_TRANSITION is a legacy catch-all. No GTM tag currently consumes it — the
// Amplitude event tags were removed, and it is noise-blocked from Medallia DXA. It
// is kept, and split below, only so each tracked transition maps to a distinct
// per-transition event that DXA *does* forward. Each transition is distinguished
// only by the `label` its dispatch site passes; keyed on the exact labels the
// submission-flow wrappers fire today.
const CASE_TRANSITION_EVENTS: Record<string, GTMEvent> = {
  'Complete Enquiry - Has Accepted Offer':               GTMEvent.COMPLETE_ENQUIRY_HAS_ACCEPTED_OFFER,
  'Complete Draft - Additional Info':                    GTMEvent.COMPLETE_DRAFT_ADDITIONAL_INFO,
  'Complete Enquiry - Date First Complained':            GTMEvent.COMPLETE_ENQUIRY_DATE_FIRST_COMPLAINED,
  'Complete Draft - EDI Survey':                         GTMEvent.COMPLETE_DRAFT_EDI_SURVEY,
  'Complete Draft - Complaint Summary':                  GTMEvent.COMPLETE_DRAFT_COMPLAINT_SUMMARY,
  'Complete Enquiry - Offer Implemented Unsatisfactory': GTMEvent.COMPLETE_ENQUIRY_OFFER_IMPLEMENTED_UNSATISFACTORY,
};

// All events go to the GTM dataLayer. GTM propagates each event to the relevant
// analytics tool (Amplitude, or Decibel Insight for DXA events). Optional chaining
// makes this a no-op when GTM is absent.
const trackCustomEvent = (eventData: EventData): void => {
  const isDevelopment = getEnv('REACT_APP_AWS_ENV') === 'development';

  // Dev-only: confirm the call was made, before the consent gate can short-circuit it.
  if (isDevelopment) {
    console.debug('[analytics] trackCustomEvent called:', eventData.event, eventData);
  }

  if (!hasAnalyticsConsent()) return;

  // Dev-only: surface each event that actually reaches the dataLayer.
  if (isDevelopment) {
    console.debug('[analytics] event:', eventData.event, eventData);
  }

  window.dataLayer?.push({ ...eventData });

  // Catch-all CASE_TRANSITION → also raise the specific per-transition event so DXA
  // can track it on its own. Safe from recursion: the mapped event is never CASE_TRANSITION.
  if (eventData.event === GTMEvent.CASE_TRANSITION && eventData.label) {
    const transitionEvent = CASE_TRANSITION_EVENTS[eventData.label];
    if (transitionEvent) {
      trackCustomEvent({ ...eventData, event: transitionEvent });
    }
  }
};

export default trackCustomEvent;
